import {
  type JointEnrollmentAccountType,
  useGetEnrollmentJointHomeAddressData,
  useSubmitEnrollmentJointHomeAddressData,
} from '@egw/api'
import {
  Alert,
  AlertType,
  Button,
  GenericError,
  Input,
  ModalDrawer,
  ProgressBar,
  Select,
  Tag,
  useModalState,
} from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useLocation, useNavigate } from 'react-router'
import { WEALTH_ROUTES } from '@egw/constants'
import { UserIcon } from '@heroicons/react/16/solid'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { statesOptions } from '../../../constants/home-address/home-address'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import {
  createAddressLines,
  getSmartyRecommendation,
  isResidentialAddress,
  isSecondaryInfoRequired,
} from '../../../utils/home-address/home-address'
import { JOINT_ENROLLMENT_ERRORS } from '../../../constants/api/error-types-joint'
import { JOINT_ACCOUNT_TYPE } from '../../../constants/joint-account-types'

const log = new Logger('Enrollment - Joint - HomeAddress')

interface FormData {
  addressLine1: string
  addressLine2: string
  addressCity: string
  addressRegionCode: string
  addressPostalCode: string
}

export const HomeAddressPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const token = query.get('token')

  const dataLoadPayload = useMemo(() => {
    if (token) {
      return {
        activePerson: {
          phoneMagicLinkCode: token ?? '',
        },
      }
    }
    return undefined
  }, [token])

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  const { data, isLoading, error } = useGetEnrollmentJointHomeAddressData(dataLoadPayload)
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointHomeAddressData()
  const { isOpen, closeModal, openModal } = useModalState()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint home address data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint home address form', submitError)
    }
  }, [submitError])
  const [recommendedAddress, setRecommendedAddress] = useState<FormData | null>(null)
  const [continueButtonIsLoading, setContinueButtonIsLoading] = useState<boolean>(false)
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)

  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const profileNameJoint = data?.jointPerson.name?.firstName ? `${data.jointPerson.name.firstName}'s Profile` : ''
  const applicationId = data && !data.isError ? data.activePerson?.applicationId : ''

  const {
    control,
    watch,
    getValues,
    reset,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      addressLine1: '',
      addressLine2: '',
      addressCity: '',
      addressRegionCode: '',
      addressPostalCode: '',
    },
  })

  // Update form values when data is loaded
  // We're prefilling the form with the data from the active person if they are the initiator
  // If they are not the initiator, we're prefilling the form with the data from the joint person
  useEffect(() => {
    if (!data?.isError && data?.activePerson) {
      if (!data.activePerson.isInitiator && isAddressDisabled) {
        reset({
          addressLine1: data.jointPerson.addressLine1 || '',
          addressLine2: data.jointPerson.addressLine2 || '',
          addressCity: data.jointPerson.addressCity || '',
          addressRegionCode: data.jointPerson.addressRegionCode || '',
          addressPostalCode: data.jointPerson.addressPostalCode || '',
        })
      } else {
        reset({
          addressLine1: data.activePerson.addressLine1 || '',
          addressLine2: data.activePerson.addressLine2 || '',
          addressCity: data.activePerson.addressCity || '',
          addressRegionCode: data.activePerson.addressRegionCode || '',
          addressPostalCode: data.activePerson.addressPostalCode || '',
        })
      }
    }
  }, [data, reset])

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - HomeAddress')

  // Form is disabled if one of these 2 account types
  const isAddressDisabled =
    !data?.activePerson?.isInitiator &&
    (data?.accountType?.includes(JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY as JointEnrollmentAccountType) ?? false)

  console.log(
    'isAddressDisabled',
    !data?.activePerson?.isInitiator,
    JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY,
    data?.accountType,
  )

  const addressLine1 = watch('addressLine1')
  const addressLine2 = watch('addressLine2')
  const addressCity = watch('addressCity')
  const addressRegionCode = watch('addressRegionCode')
  const addressPostalCode = watch('addressPostalCode')

  useEffect(() => {
    if (!hasShownIntent && (addressLine1 || addressLine2 || addressCity || addressRegionCode || addressPostalCode)) {
      setHasShownIntent(true)
      sendEvent('bankmulti_enroll_address_intent', { applicationId })
    }
  }, [hasShownIntent, addressLine1, addressLine2, addressCity, addressRegionCode, addressPostalCode])

  const onSubmit = async (): Promise<void> => {
    const payload = recommendedAddress ? recommendedAddress : getValues()

    try {
      const response = await mutate({
        activePerson: { ...payload, addressCountry: 'US' },
      })

      // Check for API errors in the response
      if (response && handleEnrollmentErrors(response)) {
        return
      }

      // If path exists and is different
      if (response?.activePerson.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_ADDRESS_VERIFIED, { applicationId })
        void navigate(response.activePerson.path)
      } else if (!response?.activePerson.path) {
        // If no path from BE, redirect to error
        log.error('No path returned from backend, redirecting to error page')
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting home address form', e)
    }
  }

  const onContinue = async (): Promise<void> => {
    const formData = getValues()
    setContinueButtonIsLoading(true)

    const smartyRecommendedAddress = await getSmartyRecommendation({
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      addressCity: formData.addressCity,
      addressRegionCode: formData.addressRegionCode,
      addressPostalCode: formData.addressPostalCode,
    })

    const isAddressWithIssues = smartyRecommendedAddress
      ? isSecondaryInfoRequired(smartyRecommendedAddress.analysis)
      : false

    if (!smartyRecommendedAddress || (isAddressWithIssues && Boolean(formData.addressLine2))) {
      setContinueButtonIsLoading(false)
      setRecommendedAddress(null)
      return
    }

    if (isSecondaryInfoRequired(smartyRecommendedAddress.analysis)) {
      setContinueButtonIsLoading(false)
      return
    }

    // check if address is not Residential (PO Box, commercial)
    if (!isResidentialAddress(smartyRecommendedAddress.analysis, smartyRecommendedAddress.metadata)) {
      setContinueButtonIsLoading(false)
      return
    }

    setRecommendedAddress(createAddressLines(smartyRecommendedAddress.components))
    setContinueButtonIsLoading(false)
  }

  const handleContinue = async (): Promise<void> => {
    await onContinue()
    openModal()
  }

  return (
    <EnrollmentJointLayout title={t('joint.home-address.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error && !data?.isError ? (
        <>
          <form className="w-full flex-1 flex flex-col justify-start">
            {profileName ? (
              <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
                {profileName}
              </Tag>
            ) : null}
            <PageHeader
              title={t('joint.home-address.title')}
              // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.VERIFY_PHONE.path}`}
            />
            <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta mb-xl">{t('joint.home-address.description')}</p>

            {/* If Address is disabled, show this alert to explain why */}
            {isAddressDisabled ? (
              <Alert
                alertType={AlertType.Info}
                description={t('joint.home-address.address_disabled_message', { accountHolder: profileNameJoint })}
                showIcon
                className="mb-xl"
              />
            ) : null}
            <div className="flex flex-col gap-sm">
              <Controller
                name="addressLine1"
                control={control}
                rules={{
                  required: t('common.required_field'),
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('joint.home-address.address_line_1')}
                    required
                    error={formErrors.addressLine1?.message}
                    testId="addressLine1_input"
                    isPII
                    disabled={isAddressDisabled}
                  />
                )}
              />

              <Controller
                name="addressLine2"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('joint.home-address.address_line_2')}
                    error={formErrors.addressLine2?.message}
                    testId="addressLine2_input"
                    isPII
                    disabled={isAddressDisabled}
                  />
                )}
              />

              <Controller
                name="addressCity"
                control={control}
                rules={{
                  required: t('common.required_field'),
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('joint.home-address.city')}
                    required
                    error={formErrors.addressCity?.message}
                    testId="addressCity_input"
                    isPII
                    disabled={isAddressDisabled}
                  />
                )}
              />

              <div className="flex gap-sm w-full">
                <div className="w-1/2">
                  <Controller
                    name="addressRegionCode"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={statesOptions}
                        label={t('joint.home-address.state')}
                        required
                        error={formErrors.addressRegionCode?.message}
                        className="w-full"
                        testId="addressRegionCode_input"
                        isPII
                        disabled={isAddressDisabled}
                      />
                    )}
                  />
                </div>
                <div className="w-1/2">
                  <Controller
                    name="addressPostalCode"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label={t('joint.home-address.zip')}
                        required
                        error={formErrors.addressCity?.message}
                        className="w-full"
                        maxLength={10}
                        testId="addressPostalCode_input"
                        isPII
                        disabled={isAddressDisabled}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 md:hidden" />
            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}
            <Button
              type="button"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={continueButtonIsLoading}
              testId="primaryAction_button"
              onClick={() => {
                if (isAddressDisabled) {
                  void onSubmit()
                } else {
                  void handleContinue()
                }
              }}
            >
              {t('common.continue')}
            </Button>
            {/* AUTO GENERATED COMPONENTS */}
          </form>

          <ModalDrawer title={t('joint.home-address.confirm_address')} isOpen={isOpen} onClose={closeModal}>
            {!recommendedAddress ? (
              <p className="typo-primary-base-regular text-text-header mb-md">
                {t('joint.home-address.couldn_find_address')}
              </p>
            ) : null}

            <p className="typo-primary-base-semibold text-text-header mb-2xs fs-exclude">
              {t('joint.home-address.you_entered')}
            </p>
            <p className="typo-primary-base-regular text-text-body mb-3xs fs-exclude">{getValues().addressLine1}</p>
            <p className="typo-primary-base-regular text-text-body mb-3xs fs-exclude">{getValues().addressLine2}</p>
            <p className="typo-primary-base-regular text-text-body fs-exclude">
              {getValues().addressCity}, {getValues().addressRegionCode} {getValues().addressPostalCode}
            </p>

            {recommendedAddress ? (
              <>
                <p className="typo-primary-base-semibold text-text-header mt-md mb-2xs">
                  {t('joint.home-address.we_found')}
                </p>
                <p className="typo-primary-base-regular text-text-body mb-3xs fs-exclude">
                  {recommendedAddress.addressLine1}
                </p>
                <p className="typo-primary-base-regular text-text-body mb-3xs fs-exclude">
                  {recommendedAddress.addressLine2}
                </p>
                <p className="typo-primary-base-regular text-text-body fs-exclude">
                  {recommendedAddress.addressCity}, {recommendedAddress.addressRegionCode}{' '}
                  {recommendedAddress.addressPostalCode}
                </p>
              </>
            ) : null}

            {recommendedAddress ? (
              <Button
                className="w-full mt-lg mb-sm"
                onClick={() => {
                  void onSubmit()
                }}
                testId="btnUseFound_button"
                isLoading={isSubmitting}
              >
                {t('joint.home-address.use_address')}
              </Button>
            ) : null}

            {!recommendedAddress ? (
              <Button
                className="w-full mt-lg mb-sm"
                onClick={() => {
                  void onSubmit()
                }}
                testId="btnUseFound_button"
                isLoading={isSubmitting}
              >
                {t('common.continue')}
              </Button>
            ) : null}

            <Button testId="btnMakeChanges_button" variant="secondary" onClick={closeModal}>
              {t('joint.home-address.make_changes')}
            </Button>
          </ModalDrawer>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}

      {/* This error handles the case when token has expired */}
      {data?.isError && data.errors.includes(JOINT_ENROLLMENT_ERRORS.INVALID_TOKEN) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.invalid_magic_link')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
